import { PlusIcon } from '@heroicons/react/24/outline';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useAutoDrawerStore } from '@/sections/insights/aiAssistant/stores/autoDrawer.store';
import { HuntStatusChip } from '@/sections/insights/hunts/HuntStatusChip';
import type { Hunt } from '@/types';

interface SidebarHuntsTabProps {
  hunts: Hunt[];
  activeHuntUuid: string | null;
  onNewHunt: () => void;
}

export function SidebarHuntsTab({
  hunts,
  activeHuntUuid,
  onNewHunt,
}: SidebarHuntsTabProps) {
  return (
    <>
      <div className="border-chariot-stroke border-b px-4 py-3">
        <Button
          onClick={onNewHunt}
          variant="outline"
          size="sm"
          className="w-full rounded-full"
        >
          <PlusIcon className="size-4" />
          New Hunt
        </Button>
      </div>
      <div className="flex-1 overflow-y-auto px-2 py-2">
        {hunts.length === 0 ? (
          <p className="text-chariot-text-secondary py-8 text-center text-xs">
            No hunts yet
          </p>
        ) : (
          hunts.map((hunt, index: number) => (
            <button
              key={hunt.uuid}
              type="button"
              onClick={() => {
                // NOTE: the `phase` argument below is currently inert.
                // AutoDrawer (HuntStatusDrawer) never reads `phase` from the
                // store — it derives its view entirely from the live
                // `activeHunt.status` returned by useHunts(). This ternary is
                // informational only and has no UI effect today; a stopped hunt
                // still renders its own "Stopped" chip regardless of the
                // 'completed' value passed here.
                useAutoDrawerStore
                  .getState()
                  .open(
                    hunt.uuid,
                    hunt.status === 'errored'
                      ? 'error'
                      : hunt.status === 'completed' ||
                          hunt.status === 'expired' ||
                          hunt.status === 'stopped'
                        ? 'completed'
                        : 'running'
                  );
              }}
              className={cn(
                'group flex w-full cursor-pointer flex-col gap-1 py-2 pr-2 pl-3 text-left text-sm transition-colors',
                hunt.uuid === activeHuntUuid
                  ? 'bg-chariot-stroke text-chariot-text-primary'
                  : 'hover:bg-chariot-background-secondary/50',
                index > 0 && 'border-chariot-stroke border-t'
              )}
              title={hunt.prompt}
            >
              <div className="flex items-start justify-between gap-2">
                <span className="text-chariot-text-primary flex-1 font-mono text-xs font-medium">
                  {hunt.uuid.slice(0, 8)}
                </span>
                <HuntStatusChip status={hunt.status} withIcon={false} />
              </div>
              <div className="text-chariot-text-secondary flex items-center gap-2 text-xs">
                <span>
                  {hunt.iterationCount} iter
                  {hunt.iterationCount === 1 ? '' : 's'}
                </span>
                <span aria-hidden>·</span>
                <span>
                  {hunt.findingsCount} finding
                  {hunt.findingsCount === 1 ? '' : 's'}
                </span>
                <span aria-hidden>·</span>
                <span>{formatRelativeDate(hunt.created)}</span>
              </div>
            </button>
          ))
        )}
      </div>
    </>
  );
}

/**
 * Same humanized-elapsed style as HuntsSidebar — kept in this file
 * because it is the only consumer and the formatting is identical by
 * design.
 */
function formatRelativeDate(dateString: string): string {
  const date = new Date(dateString);
  const diffMs = Date.now() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}
